import { useMemo, type FC } from 'react';
import type { ProductDto, ProductKind } from '@/api';
import {
  Badge,
  Table,
  type BadgeTone,
  type TableColumn,
  type TableSort,
} from '@/ui';

// Unspecified - позиция из 1С, тип которой не определён: признака «закваска или фермент» в
// номенклатуре УТ нет (architecture/contracts/onec-nomenclature-changes.md, «Потребитель»).
const KIND_LABELS: Record<ProductKind, string> = {
  Unspecified: 'Не определён',
  Starter: 'Закваска',
  Enzyme: 'Фермент',
};

const KIND_TONES: Record<ProductKind, BadgeTone> = {
  Unspecified: 'neutral',
  Starter: 'preparing',
  Enzyme: 'payment',
};

export interface ProductsTableProps {
  products: ReadonlyArray<ProductDto>;
  isLoading: boolean;
  sort: TableSort;
  onSortChange: (field: string) => void;
  emptyLabel: string;
  footer: React.ReactNode;
}

/** Таблица справочника товара. Сортировка серверная - страница перевыпускает запрос. */
export const ProductsTable: FC<ProductsTableProps> = ({
  products,
  isLoading,
  sort,
  onSortChange,
  emptyLabel,
  footer,
}) => {
  const columns = useMemo<ReadonlyArray<TableColumn<ProductDto>>>(
    () => [
      {
        id: 'code',
        header: 'Артикул',
        isSortable: true,
        mono: true,
        width: 'w-44',
        cell: (product) => (
          <span className="font-medium text-ink-700">{product.code}</span>
        ),
      },
      {
        id: 'name',
        header: 'Наименование',
        isSortable: true,
        cell: (product) => (
          <span className="font-[550] text-ink-900">{product.name}</span>
        ),
      },
      {
        id: 'group',
        header: 'Группа',
        isSortable: true,
        width: 'w-52',
        hideBelow: 'md',
        cell: (product) =>
          product.groupName ?? <span className="text-ink-400">-</span>,
      },
      {
        id: 'unit',
        header: 'Ед.',
        width: 'w-20',
        hideBelow: 'md',
        cell: (product) =>
          product.unitName ?? <span className="text-ink-400">-</span>,
      },
      {
        id: 'kind',
        header: 'Тип',
        isSortable: true,
        width: 'w-40',
        hideBelow: 'sm',
        cell: (product) => (
          <Badge tone={KIND_TONES[product.kind]}>
            {KIND_LABELS[product.kind]}
          </Badge>
        ),
      },
      {
        id: 'isActive',
        header: 'Доступность',
        width: 'w-44',
        hideBelow: 'md',
        cell: (product) =>
          product.isActive ? (
            <Badge tone="completed" withDot>
              Активна
            </Badge>
          ) : (
            <Badge tone="neutral" withDot>
              Снята с продажи
            </Badge>
          ),
      },
    ],
    [],
  );

  return (
    <Table
      columns={columns}
      rows={products}
      getRowKey={(product) => product.id}
      isLoading={isLoading}
      sort={sort}
      onSortChange={onSortChange}
      emptyLabel={emptyLabel}
      footer={footer}
    />
  );
};
